import type {
  CreateWebhookReceiverRequest,
  UpdateWebhookReceiverRequest,
  WebhookEventType,
  WebhookReceiver,
  WebhookReceiverScope,
} from '@/types/webhook';

// A Record so tsc flags a WebhookEventType without a label.
const EVENT_TYPE_LABELS: Record<WebhookEventType, string> = {
  'app.status_changed': 'Status changed',
  'app.deployed': 'Deployed',
  'app.deploy_failed': 'Deploy failed',
  'app.migrated': 'Migrated',
  'app.migration_failed': 'Migration failed',
  'app.domain_switch_started': 'Domain switch started',
  'app.domain_switched': 'Domain switched',
  'app.domain_switch_failed': 'Domain switch failed',
  'app.deleted': 'Deleted',
  'domain.ready_changed': 'Domain readiness changed',
};

export const EVENT_TYPES: readonly { type: WebhookEventType; label: string }[] = (
  Object.keys(EVENT_TYPE_LABELS) as WebhookEventType[]
).map((type) => ({ type, label: EVENT_TYPE_LABELS[type] }));

const labelOf = (t: string) => EVENT_TYPES.find((e) => e.type === t)?.label ?? t;

/** "All events" for an empty filter, otherwise up to two labels and a +N. */
export function eventTypesLabel(types: readonly string[] | null | undefined): string {
  if (!types || types.length === 0) return 'All events';
  const shown = types.slice(0, 2).map(labelOf).join(', ');
  return types.length > 2 ? `${shown} +${types.length - 2}` : shown;
}

export function receiverScopeLabel(apiKeyId: number | null, keyNames: ReadonlyMap<number, string>): string {
  if (apiKeyId === null) return 'Global';
  const name = keyNames.get(apiKeyId);
  return name ? `Key: ${name}` : `Key #${apiKeyId}`;
}

// details.code of the 409s the webhook endpoints return.
const conflictMessages = new Map<string, string>([
  ['RECEIVER_INACTIVE', 'This receiver is inactive or deleted. Activate it (or recreate it) before sending.'],
  ['API_KEY_DELETED', "This receiver's API key was deleted, so it can't be reactivated."],
  ['DELIVERY_PENDING', 'This delivery is still pending. Wait for it to finish before redelivering.'],
]);

/**
 * User-facing message for an error from api.webhooks. Known `details.code`s get
 * a friendly message; string details (validation reasons such as "url must use
 * https") are appended to the error's message; anything else gets its own
 * message, or `fallback` if it has none or is not an Error.
 * Duck-typed on ApiError's shape so it stays importable without lib/api.
 */
export function webhookErrorMessage(err: unknown, fallback = 'Request failed'): string {
  if (!(err instanceof Error)) return fallback;
  const details = (err as { details?: unknown }).details;
  if (details !== null && typeof details === 'object') {
    const code = (details as { code?: unknown }).code;
    const known = typeof code === 'string' ? conflictMessages.get(code) : undefined;
    if (known) return known;
  }
  const message = err.message || fallback;
  if (typeof details === 'string' && details.trim()) return `${message}: ${details.trim()}`;
  return message;
}

/** SecretRevealDialog texts for a webhook signing secret (create and rotate). */
export const SIGNING_SECRET_ACK = 'I have copied this signing secret and stored it somewhere safe.';
export const SIGNING_SECRET_WARNING =
  'Use this secret to verify the X-Bridge-Signature header. If you lose it, rotate it and update your receiver.';

/** State of the create/edit receiver form. */
export interface WebhookReceiverForm {
  name: string;
  url: string;
  scope: WebhookReceiverScope;
  /** Select value of the chosen API key; '' when none is chosen. Create only. */
  apiKeyId: string;
  /** Kept in EVENT_TYPES order; [] = every event. */
  eventTypes: WebhookEventType[];
  /** Edit only. */
  isActive: boolean;
}

export const EMPTY_RECEIVER_FORM: WebhookReceiverForm = {
  name: '',
  url: '',
  scope: 'global',
  apiKeyId: '',
  eventTypes: [],
  isActive: true,
};

const typeOrder = (t: WebhookEventType) => EVENT_TYPES.findIndex((e) => e.type === t);

const knownTypes = new Set<string>(Object.keys(EVENT_TYPE_LABELS));

/**
 * Name and URL are trimmed, as they are on submit. Event types this UI does not
 * know (e.g. added by a newer server) are dropped, since there is no checkbox to
 * show them; updateReceiverRequest compares against this same normalised form,
 * so they are only overwritten when the user actually changes the events.
 */
export function receiverToForm(r: WebhookReceiver): WebhookReceiverForm {
  return {
    name: r.name.trim(),
    url: r.url.trim(),
    scope: r.api_key_id === null ? 'global' : 'api_key',
    apiKeyId: r.api_key_id === null ? '' : String(r.api_key_id),
    eventTypes: (r.event_types ?? []).filter((t) => knownTypes.has(t)).sort((a, b) => typeOrder(a) - typeOrder(b)),
    isActive: r.is_active,
  };
}

/** Adds or removes `type`, keeping the result in EVENT_TYPES order. */
export function toggleEventType(types: readonly WebhookEventType[], type: WebhookEventType, checked: boolean): WebhookEventType[] {
  const set = new Set(types);
  if (checked) set.add(type);
  else set.delete(type);
  return EVENT_TYPES.map((e) => e.type).filter((t) => set.has(t));
}

export type ReceiverFormField = 'name' | 'url' | 'apiKeyId';

export interface ReceiverFormError {
  field: ReceiverFormField;
  message: string;
}

/** Client-side check before submit; null when the form can be sent. The server validates the URL itself. */
export function receiverFormError(form: WebhookReceiverForm, mode: 'create' | 'edit'): ReceiverFormError | null {
  if (!form.name.trim()) return { field: 'name', message: 'Name is required.' };
  if (!form.url.trim()) return { field: 'url', message: 'URL is required.' };
  if (mode === 'create' && form.scope === 'api_key' && !form.apiKeyId) {
    return { field: 'apiKeyId', message: 'Select an API key for an API key receiver.' };
  }
  return null;
}

/**
 * Create body. An api_key receiver always names its key: with the master key
 * the server requires it, and otherwise it would default to the caller's key.
 */
export function createReceiverRequest(form: WebhookReceiverForm): CreateWebhookReceiverRequest {
  const req: CreateWebhookReceiverRequest = {
    name: form.name.trim(),
    url: form.url.trim(),
    scope: form.scope,
    event_types: [...form.eventTypes],
  };
  if (form.scope === 'api_key') req.api_key_id = Number(form.apiKeyId);
  return req;
}

const sameTypes = (a: readonly string[], b: readonly string[]) => {
  const set = new Set(a);
  return set.size === new Set(b).size && b.every((t) => set.has(t));
};

/**
 * Update body with only the fields the user changed; {} when nothing changed.
 * Compared against receiverToForm(original), so the load-time normalisation
 * (trimming, dropping unknown event types) is never sent on its own.
 */
export function updateReceiverRequest(original: WebhookReceiver, form: WebhookReceiverForm): UpdateWebhookReceiverRequest {
  const base = receiverToForm(original);
  const req: UpdateWebhookReceiverRequest = {};
  const name = form.name.trim();
  const url = form.url.trim();
  if (name !== base.name) req.name = name;
  if (url !== base.url) req.url = url;
  if (!sameTypes(form.eventTypes, base.eventTypes)) req.event_types = [...form.eventTypes];
  if (form.isActive !== base.isActive) req.is_active = form.isActive;
  return req;
}
